/**
 * Magic-link hand-off from an in-app browser to Safari.
 *
 * A link opened in Telegram signs in inside Telegram's own browser; "Open in Safari" then opens the CURRENT address,
 * and Safari can sign in only if that address still carries the token. It used to carry it on the first screen only:
 * any move to another section (history, settings) dropped the query, and Safari opened without a token on the login
 * screen (21.10.2026). So for as long as the server still accepts the token (a short window after its first use),
 * every address in this tab keeps it. The window is counted from our own sign-in; when it is over, or on sign-out,
 * the token is forgotten and stops being added.
 */
const KEY = 'tacita.magic-handoff';
/** The server accepts a used magic token again for 10 minutes (ShowcaseCredentialsService). */
export const HANDOFF_WINDOW_MS = 10 * 60 * 1000;

interface Stored { token: string; until: number }

/** Remember the token that has just signed this tab in. Per tab (sessionStorage): another tab never inherits it. */
export function rememberHandoffToken(token: string, now: number = Date.now()): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ token, until: now + HANDOFF_WINDOW_MS } satisfies Stored));
  } catch { /* storage blocked — the first screen still carries the token */ }
}

/** The token to keep in addresses, or null when there is none or its window is over. */
export function handoffToken(now: number = Date.now()): string | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as Partial<Stored>;
    if (typeof s.token === 'string' && s.token && typeof s.until === 'number' && now < s.until) return s.token;
    sessionStorage.removeItem(KEY);
  } catch { /* storage blocked or a broken value */ }
  return null;
}

export function forgetHandoffToken(): void {
  try { sessionStorage.removeItem(KEY); } catch { /* storage blocked */ }
}
